import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, X, ShieldCheck, AlertCircle, Users, Sparkles, Copy } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const ACTION_LABELS = { view: 'Ver', create: 'Crear', edit: 'Editar', delete: 'Eliminar', publish: 'Publicar' };

// Quick-start permission presets. 'all' = every action the group supports.
const TEMPLATES = [
  { id: 'content-editor', label: 'Editor de contenido', groups: { news: 'all', events: 'all', pages: 'all', periodicals: 'all', media: 'all', translations: ['view', 'edit'] } },
  { id: 'forum-mod', label: 'Moderador de foro', groups: { forum: 'all' } },
  { id: 'interpreters-mgr', label: 'Gestor de intérpretes', groups: { interpreters: 'all', dues: 'all', profile_edits: 'all' } },
  { id: 'comms', label: 'Mensajes y newsletter', groups: { contact: 'all', newsletter: 'all' } },
  { id: 'registrations', label: 'Solicitudes de miembros', groups: { registrations: 'all' } },
  { id: 'readonly', label: 'Solo lectura (todo)', groups: 'view-all' },
];

export default function AdminRolesPage() {
  const [roles, setRoles] = useState([]);
  const [permGroups, setPermGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | roleId | null
  const [form, setForm] = useState({ name: '', permissions: [] });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get('/admin/staff-roles'),
        api.get('/admin/users/permissions'),
      ]);
      setRoles(r.data || []);
      setPermGroups(p.data.groups || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEditing('new'); setForm({ name: '', permissions: [] }); setError(''); }
  function openEdit(r) { setEditing(r.id); setForm({ name: r.name, permissions: r.permissions || [] }); setError(''); }
  function duplicate(r) {
    setEditing('new');
    setForm({ name: `${r.name} (copia)`, permissions: [...(r.permissions || [])] });
    setError('');
  }
  function close() { setEditing(null); setError(''); }

  function hasGrant(group, action) {
    const arr = form.permissions || [];
    return arr.includes(group) || arr.includes(`${group}:${action}`);
  }
  function toggleAction(group, action) {
    const arr = form.permissions || [];
    const granular = `${group}:${action}`;
    let next = arr;
    if (arr.includes(group)) {
      next = arr.filter((p) => p !== group);
      const g = permGroups.find((x) => x.key === group);
      (g?.actions || []).forEach((a) => { if (a !== action) next = [...next, `${group}:${a}`]; });
    } else if (arr.includes(granular)) {
      next = arr.filter((p) => p !== granular);
    } else {
      next = [...arr, granular];
    }
    setForm({ ...form, permissions: next });
  }
  function setGroupAll(group, on) {
    const arr = (form.permissions || []).filter((p) => p !== group && !p.startsWith(`${group}:`));
    if (!on) { setForm({ ...form, permissions: arr }); return; }
    const g = permGroups.find((x) => x.key === group);
    setForm({ ...form, permissions: [...arr, ...(g?.actions || []).map((a) => `${group}:${a}`)] });
  }

  function applyTemplate(tpl) {
    const perms = [];
    if (tpl.groups === 'view-all') {
      permGroups.filter((g) => g.key !== 'admin').forEach((g) => {
        if ((g.actions || []).includes('view')) perms.push(`${g.key}:view`);
      });
    } else {
      Object.entries(tpl.groups).forEach(([gk, acts]) => {
        const gdef = permGroups.find((g) => g.key === gk);
        if (!gdef) return;
        const list = acts === 'all' ? (gdef.actions || []) : acts.filter((a) => (gdef.actions || []).includes(a));
        list.forEach((a) => perms.push(`${gk}:${a}`));
      });
    }
    setForm((f) => ({ ...f, permissions: Array.from(new Set(perms)) }));
  }

  async function save() {
    setSaving(true); setError('');
    try {
      const payload = { name: form.name.trim(), permissions: form.permissions };
      if (editing === 'new') {
        await api.post('/admin/staff-roles', payload);
        setFeedback({ type: 'success', msg: `Rol "${payload.name}" creado.` });
      } else {
        await api.put(`/admin/staff-roles/${editing}`, payload);
        setFeedback({ type: 'success', msg: 'Rol actualizado. Los permisos se aplicaron a sus usuarios.' });
      }
      await load();
      close();
      setTimeout(() => setFeedback(null), 6000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(r) {
    if (!window.confirm(`¿Eliminar el rol "${r.name}"?`)) return;
    try {
      await api.delete(`/admin/staff-roles/${r.id}`);
      setFeedback({ type: 'success', msg: 'Rol eliminado.' });
      await load();
      setTimeout(() => setFeedback(null), 5000);
    } catch (e) {
      setFeedback({ type: 'error', msg: formatApiError(e.response?.data?.detail) });
      setTimeout(() => setFeedback(null), 7000);
    }
  }

  return (
    <div data-testid="admin-roles-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-charcoal-800 flex items-center gap-2">
            <ShieldCheck size={20} className="text-cmic-600" /> Roles personalizados
          </h1>
          <p className="text-sm text-charcoal-500 mt-0.5">
            Crea roles con permisos a la medida y asígnalos a usuarios del panel. No pertenecen al consejo directivo.
          </p>
        </div>
        <button onClick={openNew} data-testid="admin-roles-new-btn"
          className="inline-flex items-center gap-2 bg-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700">
          <Plus size={16} /> Nuevo rol
        </button>
      </div>

      {feedback && (
        <div data-testid="admin-roles-feedback" className={`p-3 rounded-lg text-sm ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}>
          {feedback.msg}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-cmic-500" size={26} /></div>
      ) : roles.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center text-charcoal-500">
          Aún no hay roles personalizados. Crea uno para empezar.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {roles.map((r) => (
            <div key={r.id} data-testid={`admin-role-card-${r.id}`} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-charcoal-800 break-words">{r.name}</div>
                  <div className="text-[11px] text-charcoal-400 font-mono">{r.key}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => duplicate(r)} data-testid={`admin-role-duplicate-${r.id}`} title="Duplicar"
                    className="p-1.5 rounded-lg text-charcoal-500 hover:bg-cmic-50 hover:text-cmic-600"><Copy size={14} /></button>
                  <button onClick={() => openEdit(r)} data-testid={`admin-role-edit-${r.id}`} title="Editar"
                    className="p-1.5 rounded-lg text-charcoal-500 hover:bg-cmic-50 hover:text-cmic-600"><Pencil size={14} /></button>
                  <button onClick={() => remove(r)} data-testid={`admin-role-delete-${r.id}`} title="Eliminar"
                    className="p-1.5 rounded-lg text-charcoal-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(r.permissions || []).slice(0, 8).map((p) => (
                  <span key={p} className="bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-mono text-charcoal-600">{p}</span>
                ))}
                {(r.permissions || []).length > 8 && (
                  <span className="text-[10px] text-charcoal-400">+{r.permissions.length - 8} más</span>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-charcoal-500 flex items-center gap-1.5">
                <Users size={12} /> {r.user_count || 0} usuario(s) con este rol
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-bold text-charcoal-800">{editing === 'new' ? 'Nuevo rol personalizado' : 'Editar rol'}</h2>
              <button onClick={close} className="p-1.5 rounded-lg text-charcoal-400 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
              {error && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 p-3 rounded-lg" data-testid="admin-roles-modal-error">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /><p className="text-sm">{error}</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-charcoal-600 mb-1">Nombre del rol *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="admin-roles-name" placeholder="ej. Editor de contenido"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-charcoal-600 mb-2">Plantillas rápidas</label>
                <div className="flex flex-wrap gap-2 mb-1" data-testid="admin-roles-templates">
                  {TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      data-testid={`admin-roles-template-${tpl.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-cmic-200 bg-cmic-50 text-cmic-700 px-3 py-1.5 hover:bg-cmic-100 transition-colors"
                    >
                      <Sparkles size={12} /> {tpl.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-charcoal-400">Precargan permisos comunes. Puedes ajustarlos abajo antes de guardar.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-charcoal-600 mb-2">Permisos *</label>
                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-charcoal-600 w-[42%]">Área</th>
                        <th className="text-center px-2 py-2 font-medium text-charcoal-600">Todo</th>
                        {['view', 'create', 'edit', 'delete', 'publish'].map((a) => (
                          <th key={a} className="text-center px-2 py-2 font-medium text-charcoal-500 text-[11px]">{ACTION_LABELS[a]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {permGroups.filter((g) => g.key !== 'admin').map((g) => {
                        const allOn = (g.actions || []).every((a) => hasGrant(g.key, a));
                        return (
                          <tr key={g.key} className="border-b border-gray-100 last:border-0">
                            <td className="px-3 py-2 text-charcoal-700">{g.label || g.key}</td>
                            <td className="px-2 py-2 text-center">
                              <input type="checkbox" checked={allOn} onChange={(e) => setGroupAll(g.key, e.target.checked)}
                                data-testid={`admin-roles-perm-all-${g.key}`} className="rounded text-cmic-500" />
                            </td>
                            {['view', 'create', 'edit', 'delete', 'publish'].map((a) => {
                              const supported = (g.actions || []).includes(a);
                              return (
                                <td key={a} className="px-2 py-2 text-center">
                                  {supported ? (
                                    <input type="checkbox" checked={hasGrant(g.key, a)} onChange={() => toggleAction(g.key, a)}
                                      data-testid={`admin-roles-perm-${g.key}-${a}`} className="rounded text-cmic-500" />
                                  ) : <span className="text-gray-300">—</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-charcoal-400 mt-1">La configuración del sitio y la gestión de usuarios permanecen exclusivas del super-administrador.</p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
              <button onClick={save} disabled={saving || !form.name.trim() || (form.permissions || []).length === 0}
                data-testid="admin-roles-save"
                className="inline-flex items-center gap-2 bg-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Guardar rol
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
